import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import { LayoutGroup, MotionConfig, motion } from 'framer-motion';
import { Bell, ChevronRight, Keyboard, LogOut, Menu, Monitor, Moon, Search, ShieldCheck, Sun, X } from 'lucide-react';
import { useAuth } from '@/app/auth';
import { useToast } from '@/app/toast';
import { useDashboard, useLicenseInsights } from '@/lib/queries';
import { applyTheme, readThemePref, type ThemePref } from '@/lib/theme';
import { num } from '@/lib/format';
import { useDeckSpotlight } from '@/lib/motion';
import { HeaderLockup, SidebarLockup } from './brand';
import { AnniversaryBanner } from './anniversary';
import { CommandPalette } from './CommandPalette';
import { ROLE_LABEL } from './welcome';
import { AssistantLauncher } from '@/features/assistant/AssistantLauncher';
import { QuickActions } from './QuickActions';
import { NAV, NAV_GROUPS, navItemFor, type NavItem } from './nav';
import { Avatar } from './ui';

// ── Breadcrumbs: pages may name the last crumb (e.g. an asset tag) ──

interface Crumb {
  label: string;
  to?: string;
}
const CrumbContext = createContext<(c: Crumb[] | null) => void>(() => {});
export function useCrumbs(crumbs: Crumb[] | null) {
  const set = useContext(CrumbContext);
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    set(crumbs);
    return () => set(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, set]);
}

function defaultCrumbs(pathname: string, items: NavItem[]): Crumb[] {
  const item = navItemFor(pathname, items) ?? navItemFor(pathname, NAV);
  return item ? [{ label: item.label, to: item.to }] : [];
}


export function AppShell() {
  const { session, signOut, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  useDeckSpotlight();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [crumbs, setCrumbs] = useState<Crumb[] | null>(null);
  const items = useMemo(() => NAV.filter((n) => can(n.capability)), [can]);
  const staff = can('inventory.read');
  const mainRef = useRef<HTMLElement>(null);

  // Move focus to the page on navigation so screen-reader and keyboard users land on content.
  useEffect(() => {
    setDrawerOpen(false);
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Keyboard: Ctrl/⌘+K or "/" opens the palette; "g" then a letter navigates; "?" lists shortcuts.
  const chord = useRef<number>(0);
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '/') {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (e.key === '?') {
        setHelpOpen(true);
      } else if (e.key === 'g') {
        chord.current = Date.now();
      } else if (Date.now() - chord.current < 1200) {
        const hit = items.find((n) => n.shortcut?.toLowerCase() === `g ${e.key.toLowerCase()}`);
        chord.current = 0;
        if (hit) navigate(hit.to);
      }
    },
    [items, navigate],
  );
  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  if (!session) return null;
  const user = session.user;
  const showSidebar = items.length > 1;
  const trail = crumbs ?? defaultCrumbs(location.pathname, items);

  const sidebar = (layout: string) => (
    <nav aria-label="Primary" className="flex h-full flex-col">
      <div className="px-5 pt-5 pb-5">
        <Link to="/" className="relative block" aria-label="SmartKargo Smart Assets home">
          <SidebarLockup />
        </Link>
      </div>
      <LayoutGroup id={layout}>
        <div className="flex-1 space-y-4 overflow-y-auto px-3 pb-3">
          {NAV_GROUPS.map((group) => {
            const groupItems = items.filter((n) => n.group === group.key);
            if (!groupItems.length) return null;
            return (
              <div key={group.key} role="group" aria-label={group.label}>
                <p className="eyebrow mb-1.5 flex items-center gap-1.5 px-2.5">
                  <group.icon aria-hidden className="size-3 opacity-70" strokeWidth={2} />
                  {group.label}
                </p>
                <ul className="space-y-0.5">
                  {groupItems.map((n) => (
                    <li key={n.to}>
                      <NavLink
                        to={n.to}
                        end={n.end}
                        className={({ isActive }) =>
                          `group relative flex h-[34px] items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 text-[13px] transition-colors duration-150 ${
                            isActive ? 'font-medium text-fg' : 'text-fg-2 hover:bg-surface-2 hover:text-fg'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && (
                              <motion.span
                                layoutId="nav-active"
                                aria-hidden
                                className="absolute inset-0 rounded-[var(--radius-md)] bg-[color-mix(in_srgb,var(--brand)_13%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--brand)_28%,transparent)] ring-inset"
                                transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.9 }}
                              >
                                <span className="absolute top-2 bottom-2 -left-3 w-[3px] rounded-r-full bg-brand" />
                              </motion.span>
                            )}
                            <n.icon
                              aria-hidden
                              className={`relative size-4 transition-transform duration-200 ease-[var(--ease-out)] group-hover:translate-x-px ${isActive ? 'text-brand' : ''}`}
                              strokeWidth={1.75}
                            />
                            <span className="relative flex-1">{n.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </LayoutGroup>
      <div className="border-t border-line p-3">
        {items
          .filter((n) => n.group === 'System')
          .map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `group mb-2 flex h-[34px] items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 text-[13px] transition-colors duration-150 ${
                  isActive ? 'bg-[color-mix(in_srgb,var(--brand)_13%,transparent)] font-medium text-fg ring-1 ring-[color-mix(in_srgb,var(--brand)_28%,transparent)] ring-inset' : 'text-fg-2 hover:bg-surface-2 hover:text-fg'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon aria-hidden className={`size-4 transition-transform duration-500 ease-[var(--ease-out)] group-hover:rotate-90 ${isActive ? 'text-brand' : ''}`} strokeWidth={1.75} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        <div className="flex items-center gap-3 rounded-[var(--radius-md)] bg-surface-2/60 px-2.5 py-2">
          <Avatar name={user.displayName} size={30} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-fg">{user.displayName}</p>
            <p className="truncate text-2xs text-fg-3">{ROLE_LABEL[user.role] ?? user.role}</p>
          </div>
          <ShieldCheck aria-label="Managed by SmartKargo IT" className="size-4 shrink-0 text-[#ff8a57]" strokeWidth={1.75} />
        </div>
      </div>
    </nav>
  );

  return (
    <MotionConfig reducedMotion="user">
    <CrumbContext.Provider value={setCrumbs}>
      <a href="#main" className="sr-only z-[70] rounded bg-surface px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        Skip to content
      </a>
      <div className="min-h-dvh lg:flex">
        {showSidebar && (
          <aside className="scope-dark sidebar-surface sticky top-0 hidden h-dvh w-[252px] shrink-0 border-r border-black/40 lg:block">{sidebar('desktop')}</aside>
        )}

        {/* Mobile / tablet drawer */}
        {showSidebar && (
          <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="animate-fade fixed inset-0 z-40 bg-black/40 lg:hidden" />
              <Dialog.Content className="scope-dark sidebar-surface animate-drawer fixed inset-y-0 left-0 z-50 w-[272px] border-r border-black/40 shadow-[var(--shadow-pop)] focus:outline-none lg:hidden" aria-describedby={undefined}>
                <Dialog.Title className="sr-only">Navigation</Dialog.Title>
                <Dialog.Close className="btn btn-ghost btn-icon absolute top-3 right-3" aria-label="Close navigation">
                  <X aria-hidden className="size-4" />
                </Dialog.Close>
                {sidebar('drawer')}
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-[color-mix(in_srgb,var(--bg)_88%,transparent)] px-4 backdrop-blur-md sm:px-6">
            {showSidebar ? (
              <button className="btn btn-ghost btn-icon -ml-2 lg:hidden" aria-label="Open navigation" onClick={() => setDrawerOpen(true)}>
                <Menu aria-hidden className="size-5" />
              </button>
            ) : (
              <Link to="/" className="mr-2 flex items-center" aria-label="SmartKargo Smart Assets home">
                <HeaderLockup />
              </Link>
            )}

            {showSidebar && (
              <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
                <ol className="flex min-w-0 items-center gap-1.5 text-[13px]">
                  <li className="hidden text-fg-3 sm:block">Smart Assets</li>
                  {trail.map((c, i) => (
                    <li key={i} className="flex min-w-0 items-center gap-1.5">
                      <ChevronRight aria-hidden className={`size-3.5 shrink-0 text-fg-3 ${i === 0 ? 'hidden sm:block' : ''}`} />
                      {c.to && i < trail.length - 1 ? (
                        <Link to={c.to} className="truncate text-fg-2 hover:text-fg">
                          {c.label}
                        </Link>
                      ) : (
                        <span aria-current="page" className="truncate font-medium text-fg">
                          {c.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </nav>
            )}
            {!showSidebar && <div className="flex-1" />}

            {staff && (
              <button
                onClick={() => setPaletteOpen(true)}
                className="hidden h-9 w-64 items-center gap-2 rounded-[var(--radius-md)] border border-line bg-surface px-3 text-[13px] text-fg-3 transition-colors hover:border-line-strong md:flex"
              >
                <Search aria-hidden className="size-4" />
                <span className="flex-1 truncate text-left">Search assets, people or pages</span>
                <span className="kbd">Ctrl K</span>
              </button>
            )}
            {staff && (
              <button className="btn btn-ghost btn-icon md:hidden" aria-label="Search" onClick={() => setPaletteOpen(true)}>
                <Search aria-hidden className="size-4" />
              </button>
            )}
            {staff && <QuickActions />}
            {staff && <Attention licences={can('insights.read')} />}
            <ThemeMenu />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="ml-1 flex items-center gap-2 rounded-full p-0.5 hover:bg-surface-2" aria-label={`Account menu for ${user.displayName}`}>
                <Avatar name={user.displayName} size={30} />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={8} className="animate-pop z-50 w-64 rounded-[var(--radius-lg)] border border-line bg-surface p-1.5 shadow-[var(--shadow-pop)]">
                  <div className="px-2.5 py-2">
                    <p className="truncate text-[13px] font-medium text-fg">{user.displayName}</p>
                    <p className="truncate text-xs text-fg-3">{user.email}</p>
                    <p className="mt-1.5 inline-block rounded-[4px] bg-surface-2 px-1.5 py-px text-2xs font-medium text-fg-2">
                      {ROLE_LABEL[user.role] ?? user.role}
                      {session.authMethod === 'development' ? ' · dev sign-in' : ''}
                    </p>
                  </div>
                  <DropdownMenu.Separator className="my-1 h-px bg-line" />
                  <DropdownMenu.Item onSelect={() => setHelpOpen(true)} className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] px-2.5 text-[13px] text-fg-2 outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-fg">
                    <Keyboard aria-hidden className="size-4" /> Keyboard shortcuts
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={async () => {
                      try {
                        await signOut();
                        navigate('/login', { replace: true });
                      } catch {
                        toast({ kind: 'error', title: 'Sign-out did not complete', detail: 'You are still signed in. Check your connection and try again.' });
                      }
                    }}
                    className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] px-2.5 text-[13px] text-fg-2 outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-fg"
                  >
                    <LogOut aria-hidden className="size-4" /> Sign out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </header>

          <main
            id="main"
            ref={mainRef}
            tabIndex={-1}
            className="mx-auto w-full max-w-[1480px] flex-1 px-4 py-6 outline-none sm:px-6 lg:px-8 lg:py-8"
            // Room under the last row so the IT Assistant launcher never covers pagination or form buttons.
            style={can('insights.read') ? { paddingBottom: 96 } : undefined}
          >
            <AnniversaryBanner />
            <div key={location.pathname} className="page-cascade">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} nav={items} canSearch={staff} />
      {can('insights.read') && <AssistantLauncher />}
      <ShortcutsDialog open={helpOpen} onOpenChange={setHelpOpen} items={items} staff={staff} />
    </CrumbContext.Provider>
    </MotionConfig>
  );
}

function ThemeMenu() {
  const [pref, setPref] = useState<ThemePref>(readThemePref);
  const choose = (p: ThemePref) => {
    setPref(p);
    applyTheme(p);
  };
  const Icon = pref === 'dark' ? Moon : pref === 'light' ? Sun : Monitor;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="btn btn-ghost btn-icon" aria-label="Theme">
        <Icon aria-hidden className="size-4" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className="animate-pop z-50 w-40 rounded-[var(--radius-lg)] border border-line bg-surface p-1.5 shadow-[var(--shadow-pop)]">
          <DropdownMenu.RadioGroup value={pref} onValueChange={(v) => choose(v as ThemePref)}>
            {(
              [
                ['light', 'Light', Sun],
                ['dark', 'Dark', Moon],
                ['system', 'System', Monitor],
              ] as const
            ).map(([v, label, I]) => (
              <DropdownMenu.RadioItem
                key={v}
                value={v}
                className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] px-2.5 text-[13px] text-fg-2 outline-none data-[highlighted]:bg-surface-2 data-[state=checked]:font-medium data-[state=checked]:text-fg"
              >
                <I aria-hidden className="size-4" /> {label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** "What needs attention": data-quality signals from SQL, not synthetic notifications. */
function Attention({ licences }: { licences: boolean }) {
  const { data } = useDashboard();
  const lic = useLicenseInsights(licences);
  const signals = (data?.dataQuality ?? []).filter((s) => s.severity !== 'info');
  // Licence renewals are their own section: software never mixes into hardware signals.
  const renewals = lic.data
    ? [
        { key: 'expired', count: lic.data.expiry.expired, label: 'Licences already expired', detail: 'In force in the register but past their expiry date.', link: '/software/licenses?expiry=expired&sort=expiryDate', danger: true },
        { key: 'due30', count: lic.data.expiry.within30, label: 'Licences expiring within 30 days', detail: 'Renew or confirm cancellation before they lapse.', link: '/software/licenses?expiry=30&sort=expiryDate', danger: false },
      ].filter((r) => r.count > 0)
    : [];
  const total = signals.length + renewals.length;
  return (
    <Popover.Root>
      <Popover.Trigger className="btn btn-ghost btn-icon relative" aria-label={`Attention items: ${total}`}>
        <Bell aria-hidden className="size-4" />
        {total > 0 && <span aria-hidden className="absolute top-2 right-2 size-2 rounded-full bg-brand ring-2 ring-[var(--bg)]" />}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} className="animate-pop z-50 w-[min(360px,calc(100vw-2rem))] rounded-[var(--radius-lg)] border border-line bg-surface shadow-[var(--shadow-pop)]">
          <div className="border-b border-line px-4 py-3">
            <p className="text-[13px] font-semibold text-fg">Needs attention</p>
            <p className="text-xs text-fg-3">Checks run continuously against the live register.</p>
          </div>
          {total === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-fg-2">Nothing needs attention right now.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
            {renewals.length > 0 && (
              <>
                <p className="eyebrow border-b border-line bg-surface-2/50 px-4 py-1.5">Software &amp; licences</p>
                <ul className="divide-y divide-line border-b border-line">
                  {renewals.map((r) => (
                    <li key={r.key}>
                      <Popover.Close asChild>
                        <Link to={r.link} className="flex items-start gap-3 px-4 py-3 hover:bg-surface-2">
                          <span className={`mono mt-px min-w-8 text-right text-[13px] font-medium tnum ${r.danger ? 'text-danger' : 'text-warn'}`}>{num(r.count)}</span>
                          <span className="min-w-0">
                            <span className="block text-[13px] text-fg">{r.label}</span>
                            <span className="block text-xs text-fg-3">{r.detail}</span>
                          </span>
                        </Link>
                      </Popover.Close>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {signals.length > 0 && <p className="eyebrow border-b border-line bg-surface-2/50 px-4 py-1.5">Hardware data quality</p>}
            <ul className="divide-y divide-line">
              {signals.map((s) => (
                <li key={s.key}>
                  <Popover.Close asChild>
                    <Link to={s.link ?? '/dashboard'} className="flex items-start gap-3 px-4 py-3 hover:bg-surface-2">
                      <span className="mono mt-px min-w-8 text-right text-[13px] font-medium text-fg tnum">{num(s.count)}</span>
                      <span className="min-w-0">
                        <span className="block text-[13px] text-fg">{s.label}</span>
                        <span className="block text-xs text-fg-3">{s.detail}</span>
                      </span>
                    </Link>
                  </Popover.Close>
                </li>
              ))}
            </ul>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ShortcutsDialog({ open, onOpenChange, items, staff }: { open: boolean; onOpenChange: (o: boolean) => void; items: NavItem[]; staff: boolean }) {
  const rows: [string, ReactNode][] = [
    ...(staff ? ([['Search / command palette', <><span className="kbd">Ctrl K</span> <span className="kbd">/</span></>]] as [string, ReactNode][]) : []),
    ...items.filter((n) => n.shortcut).map((n) => [n.label, <span className="kbd">{n.shortcut}</span>] as [string, ReactNode]),
    ['Show this list', <span className="kbd">?</span>],
  ];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade modal-overlay fixed inset-0 z-50" />
        <Dialog.Content className="modal-pop fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] border border-line bg-surface p-5 shadow-[var(--shadow-pop)] focus:outline-none">
          <Dialog.Title className="text-[15px] font-semibold text-fg">Keyboard shortcuts</Dialog.Title>
          <Dialog.Description className="sr-only">Keys that move around the Smart Assets.</Dialog.Description>
          <dl className="mt-4 space-y-2.5">
            {rows.map(([label, keys]) => (
              <div key={label} className="flex items-center justify-between gap-4 text-[13px]">
                <dt className="text-fg-2">{label}</dt>
                <dd className="flex gap-1">{keys}</dd>
              </div>
            ))}
          </dl>
          <Dialog.Close className="btn btn-secondary btn-sm mt-5 w-full">Close</Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
